<template>
  <div
    class="w-full h-full flex flex-col relative animate-fade-in"
    :class="theme.text"
  >
    <div class="shrink-0 h-12 flex items-center justify-between px-4 z-10">
      <button @click="$emit('back')">
        <icon-base
          name="ChevronLeft"
          class="w-6 h-6 opacity-60 hover:opacity-100"
        ></icon-base>
      </button>
      <span class="font-bold">{{ t.member_title }}</span>
      <div class="w-6"></div>
    </div>

    <div class="flex-1 overflow-y-auto px-4 pb-4">
      <div class="text-center mb-6 mt-1">
        <div
          class="inline-block p-2 rounded-xl bg-gradient-to-tr from-amber-400 to-orange-500 shadow-lg shadow-orange-500/30 mb-2"
        >
          <icon-base name="Crown" class="w-6 h-6 text-white"></icon-base>
        </div>
        <h2 class="font-bold mb-1" :class="isWatch ? 'text-lg' : 'text-2xl'">
          {{ t.unlock_title }}
        </h2>
        <p v-if="!isWatch" class="opacity-60 text-xs">{{ t.unlock_desc }}</p>
      </div>

      <div class="flex flex-col gap-3 mb-6">
        <div v-if="loadingGoods" class="text-center py-4 opacity-50">
          <icon-base
            name="Refresh"
            class="w-6 h-6 animate-spin mx-auto mb-2"
          ></icon-base>
          <span class="text-xs">{{ t.pay_loading_plans }}</span>
        </div>
        <div
          v-else-if="loadError"
          class="text-center py-4 opacity-60"
        >
          <div class="text-xs mb-2">{{ t.pay_load_fail }}</div>
          <button
            @click="fetchGoods"
            class="text-xs font-bold px-3 py-1.5 rounded-lg"
            :class="theme.isDark ? 'bg-white/10' : 'bg-black/5'"
          >
            {{ t.pay_retry }}
          </button>
        </div>

        <template v-else>
          <button
            v-for="item in goodsList"
            :key="item.id"
            @click="selectedId = item.id"
            class="w-full p-3 rounded-xl border-2 transition-all relative flex items-center justify-between"
            :class="
              selectedId === item.id
                ? 'border-amber-500 bg-amber-500/5'
                : theme.isDark
                ? 'border-white/10'
                : 'border-black/5'
            "
          >
            <div
              v-if="item.id === recommendedId"
              class="absolute -top-2 right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
            >
              {{ t.recommend || "RECOMMEND" }}
            </div>

            <div class="flex items-center gap-3">
              <div
                class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                :class="
                  selectedId === item.id
                    ? 'border-amber-500'
                    : 'border-gray-400'
                "
              >
                <div
                  v-if="selectedId === item.id"
                  class="w-2 h-2 rounded-full bg-amber-500"
                ></div>
              </div>
              <div class="text-left">
                <div class="font-bold text-sm">{{ item.name }}</div>
                <div
                  v-if="item.product_description"
                  class="text-[9px] text-amber-500 font-bold"
                >
                  {{ item.product_description }}
                </div>
              </div>
            </div>
            <div class="text-right">
              <div class="font-bold text-sm">
                ¥{{ item.product_price }}
                <span class="text-[10px] font-normal opacity-50">
                  {{
                    item.name.includes("月")
                      ? "/M"
                      : item.name.includes("年")
                      ? "/Y"
                      : ""
                  }}
                </span>
              </div>
            </div>
          </button>
        </template>
      </div>

      <div
        class="rounded-xl p-4 mb-4"
        :class="theme.isDark ? 'bg-white/5' : 'bg-black/5'"
      >
        <div
          class="text-[10px] font-bold opacity-40 uppercase tracking-wider mb-3"
        >
          {{ t.privilege }}
        </div>
        <div class="space-y-3">
          <div
            v-for="(priv, i) in currentPrivileges"
            :key="i"
            class="flex gap-3"
          >
            <div class="mt-0.5">
              <icon-base
                name="Check"
                class="w-3 h-3 text-amber-500"
              ></icon-base>
            </div>
            <div>
              <div class="text-xs font-bold">{{ priv.t }}</div>
              <div v-if="!isWatch" class="text-[10px] opacity-50">
                {{ priv.d }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="p-4 border-t safe-bottom"
      :class="
        theme.isDark
          ? 'border-white/10 bg-[#0f1115]'
          : 'border-black/5 bg-white'
      "
    >
      <button
        @click="openPaymentModal"
        :disabled="loading || !selectedId"
        class="w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-600 text-white font-bold text-base shadow-lg shadow-orange-500/20 active:scale-95 transition-all flex items-center justify-center gap-2"
      >
        <span v-if="!loading">{{ t.pay_btn }} {{ priceDisplay }}</span>
        <icon-base
          v-else
          name="Refresh"
          class="w-4 h-4 animate-spin"
        ></icon-base>
      </button>
      <div v-if="!isWatch" class="text-[9px] text-center opacity-40 mt-2">
        {{ t.pay_agreement }}
      </div>
    </div>

    <div
      v-if="showPaymentModal"
      class="absolute inset-0 z-50 flex items-end justify-center sm:items-center bg-black/60 backdrop-blur-sm animate-fade-in"
    >
      <div
        class="w-full max-w-sm p-6 rounded-t-2xl sm:rounded-2xl shadow-2xl relative"
        :class="
          theme.isDark ? 'bg-[#1a1c20] text-white' : 'bg-white text-black'
        "
      >
        <button
          @click="showPaymentModal = false"
          class="absolute top-4 right-4 opacity-50 hover:opacity-100"
        >
          <icon-base name="Close" class="w-5 h-5" />
        </button>

        <h3 class="text-lg font-bold mb-6 text-center">
          {{ t.pay_method_title }}
        </h3>

        <div class="flex flex-col gap-3">
          <button
            @click="confirmPay('WeChatPay')"
            class="w-full py-3.5 px-4 rounded-xl flex items-center justify-between border-2 transition-all hover:bg-green-500/5 active:scale-95"
            :class="
              theme.isDark
                ? 'border-white/10 hover:border-green-500'
                : 'border-black/5 hover:border-green-500'
            "
          >
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-[#07C160] flex items-center justify-center text-white"
              >
                <span class="font-bold text-xs">微信</span>
              </div>
              <span class="font-bold">{{ t.pay_wechat }}</span>
            </div>
            <icon-base name="ChevronRight" class="w-4 h-4 opacity-40" />
          </button>

          <button
            v-if="paymentProduct !== 'JSAPI'"
            @click="confirmPay('Alipay')"
            class="w-full py-3.5 px-4 rounded-xl flex items-center justify-between border-2 transition-all hover:bg-blue-500/5 active:scale-95"
            :class="
              theme.isDark
                ? 'border-white/10 hover:border-blue-500'
                : 'border-black/5 hover:border-blue-500'
            "
          >
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-[#1677FF] flex items-center justify-center text-white"
              >
                <span class="font-bold text-xs">支付宝</span>
              </div>
              <span class="font-bold">{{ t.pay_alipay }}</span>
            </div>
            <icon-base name="ChevronRight" class="w-4 h-4 opacity-40" />
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showQrModal"
      class="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
    >
      <div
        class="w-full max-w-xs p-6 rounded-2xl shadow-2xl relative flex flex-col items-center gap-4 animate-scale-in"
        :class="
          theme.isDark ? 'bg-[#1a1c20] text-white' : 'bg-white text-black'
        "
      >
        <button
          @click="cancelQr"
          class="absolute top-4 right-4 opacity-50 hover:opacity-100"
        >
          <icon-base name="Close" class="w-5 h-5" />
        </button>

        <h3 class="text-lg font-bold">{{ t.pay_scan_title }}</h3>

        <div class="bg-white p-2 rounded-xl">
          <qr-code :text="qrCodeText" :size="200" />
        </div>

        <div class="text-xs opacity-50 text-center">
          {{
            qrPayWay === "Alipay" ? t.pay_scan_alipay : t.pay_scan_wechat
          }}
        </div>
      </div>
    </div>

    <div
      v-if="toast"
      class="absolute bottom-24 left-1/2 -translate-x-1/2 z-[60] text-xs px-3 py-1.5 rounded-full bg-black/70 text-white animate-fade-in"
    >
      {{ toast }}
    </div>
  </div>
</template>

<script>
import {
  getGoodsList,
  createOrder,
  orderState,
  bingWechatInfo,
  wechatPayAppConfig,
} from "@/api/index";
import { isTauri } from "@/utils/platform";
import { nativeWechatPay } from "@/utils/native";
import QrCode from "./QrCode.vue";

export default {
  components: { QrCode },
  props: ["theme", "t", "device"],
  data() {
    return {
      loading: false,
      loadingGoods: true,
      loadError: false,
      goodsList: [],
      selectedId: null,
      showPaymentModal: false,
      paymentProduct: "NATIVE",

      showQrModal: false,
      qrCodeText: "",
      qrPayWay: "WeChatPay",
      polling: false,
      toast: "",
    };
  },
  computed: {
    isWatch() {
      return this.device === "WATCH";
    },
    // 推荐商品 id：优先后端 recommend/is_recommend 标记，否则价格最高，兜底列表首个。
    // 默认选中项与推荐角标保持一致。
    recommendedId() {
      const list = this.goodsList;
      if (!list.length) return null;
      const flagged = list.find(
        (i) => i.recommend || i.is_recommend || i.is_default
      );
      if (flagged) return flagged.id;
      let best = list[0];
      list.forEach((i) => {
        if (Number(i.product_price) > Number(best.product_price)) best = i;
      });
      return best.id;
    },
    selectedItem() {
      return this.goodsList.find((i) => i.id === this.selectedId);
    },
    priceDisplay() {
      return this.selectedItem ? `¥${this.selectedItem.product_price}` : "";
    },
    currentPrivileges() {
      if (!this.selectedItem?.remark) return this.t.privileges || [];
      const lines = this.selectedItem.remark
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "");
      const list = [];
      for (let i = 0; i < lines.length; i += 2) {
        list.push({
          t: lines[i],
          d: lines[i + 1] || "",
        });
      }
      return list;
    },
  },
  created() {
    this.detectEnvironment();
  },
  async mounted() {
    // 先处理微信网页授权回跳（带 code），再拉商品，最后恢复因授权中断的支付。
    await this.handleWechatAuthReturn();
    await this.fetchGoods();
    await this.resumePendingPay();
  },
  methods: {
    detectEnvironment() {
      // Tauri 壳 → APP 支付（桌面壳会自动回退扫码；移动壳集成 SDK 后直接生效）。
      if (isTauri()) {
        this.paymentProduct = "APP";
        console.log("Payment Environment Detected: APP (Tauri)");
        return;
      }
      const ua = navigator.userAgent.toLowerCase();
      const isWeChat = ua.indexOf("micromessenger") !== -1;
      const isMobile =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          ua
        );

      if (isWeChat) {
        this.paymentProduct = "JSAPI";
      } else if (isMobile) {
        this.paymentProduct = "H5";
      } else {
        this.paymentProduct = "NATIVE";
      }
      console.log("Payment Environment Detected:", this.paymentProduct);
    },

    // 微信网页授权回跳：URL 带 code 时换取并绑定 openid，然后清理 URL。
    // 即便非 JSAPI 落地也要清理残留的 code，避免刷新重复使用。
    async handleWechatAuthReturn() {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      if (!code) return;
      if (this.paymentProduct === "JSAPI") {
        try {
          await bingWechatInfo(code);
        } catch (e) {
          console.error("微信授权绑定失败", e);
        }
      }
      // 去掉 url 上的 code/state，避免刷新重复使用。
      params.delete("code");
      params.delete("state");
      const qs = params.toString();
      const clean =
        window.location.pathname +
        (qs ? "?" + qs : "") +
        window.location.hash;
      window.history.replaceState({}, "", clean);
    },

    // 保存待支付上下文（选中商品 / 支付方式 / out_trade_no）供回跳后续付或查询。幂等。
    savePending(ctx) {
      try {
        localStorage.setItem("zeno_pay_pending", JSON.stringify(ctx || {}));
      } catch (e) {
        /* ignore */
      }
    },
    readPending() {
      try {
        const raw = localStorage.getItem("zeno_pay_pending");
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    clearPending() {
      try {
        localStorage.removeItem("zeno_pay_pending");
      } catch (e) {
        /* ignore */
      }
    },

    // 回跳 / 重入后恢复待支付意图。幂等、可重入：
    // - 授权类 pending（JSAPI）→ 自动续付；
    // - 跳转类 pending（H5/支付宝，带 outTradeNo）→ 查询订单状态确认是否已支付。
    async resumePendingPay() {
      const pend = this.readPending();
      if (!pend) return;
      // 授权回跳续付：仅 JSAPI 环境有意义。
      if (pend.auth && this.paymentProduct === "JSAPI") {
        this.clearPending();
        if (pend.goodsId) this.selectedId = pend.goodsId;
        this.$nextTick(() => this.confirmPay("WeChatPay"));
        return;
      }
      // 跳转支付回来：查一次订单状态。
      if (pend.outTradeNo) {
        this.clearPending();
        if (pend.goodsId) this.selectedId = pend.goodsId;
        try {
          const s = await orderState(pend.outTradeNo);
          if (s && s.trade_state === "SUCCESS" && !this._destroyed) {
            this.onPaid();
          }
        } catch (e) {
          /* 查询失败忽略，用户可重新发起 */
        }
      }
    },

    async fetchGoods() {
      this.loadingGoods = true;
      this.loadError = false;
      try {
        const res = await getGoodsList();
        this.goodsList = Array.isArray(res) ? res : res.data || [];
        // 默认选中推荐项，保证与推荐角标一致；未被 pending 覆盖时生效。
        if (!this.selectedId && this.recommendedId) {
          this.selectedId = this.recommendedId;
        }
      } catch (e) {
        console.error("Fetch goods failed", e);
        this.loadError = true;
      } finally {
        this.loadingGoods = false;
      }
    },

    openPaymentModal() {
      if (!this.selectedId) return;
      this.showPaymentModal = true;
    },

    // 支付入口：按当前环境（paymentProduct）分发。
    async confirmPay(paymentWay) {
      this.showPaymentModal = false;
      this.loading = true;
      try {
        if (paymentWay === "WeChatPay") {
          switch (this.paymentProduct) {
            case "JSAPI":
              await this.payWeixinJsapi();
              break;
            case "APP":
              await this.payWeixinApp();
              break;
            case "H5":
              await this.payRedirect("WeChatPay");
              break;
            default:
              await this.payNativeQr("WeChatPay");
          }
        } else {
          // 支付宝：NATIVE 扫码，其余跳转收银台。
          if (this.paymentProduct === "NATIVE") await this.payNativeQr(paymentWay);
          else await this.payRedirect(paymentWay);
        }
      } catch (e) {
        if (e && e.message === "__CANCELLED__") {
          this.loading = false;
          return;
        }
        console.error("Pay failed:", e);
        this.flashToast(
          e && e.message ? `${this.t.pay_failed}: ${e.message}` : this.t.pay_failed
        );
        this.loading = false;
      }
    },

    // 统一下单。拦截器成功时直接返回 data（{ order, outTradeNo }）。
    async createOrderFor(product, way) {
      const data = await createOrder({
        payment_way: way,
        payment_product: product,
        goods_id: this.selectedId,
      });
      return { link: data.order, outTradeNo: data.outTradeNo };
    },

    // H5 / 支付宝：拿到链接直接跳转。跳转前存 pending（含 out_trade_no）供回来查询。
    async payRedirect(way) {
      const { link, outTradeNo } = await this.createOrderFor(
        this.paymentProduct,
        way
      );
      if (link) {
        this.savePending({ goodsId: this.selectedId, outTradeNo, way });
        window.location.href = link;
        return;
      }
      // 无跳转链接：不能卡在 loading。
      this.loading = false;
      this.flashToast(this.t.pay_failed);
    },

    // NATIVE：弹二维码并轮询（也是 APP 桌面壳的回退方式）。
    async payNativeQr(way) {
      const { link, outTradeNo } = await this.createOrderFor("NATIVE", way);
      this.qrCodeText = link;
      this.qrPayWay = way;
      this.showQrModal = true;
      this.polling = true;
      this._pollTimer = setTimeout(() => this.pollOrder(outTradeNo), 1500);
    },

    // JSAPI（微信内）：未授权先引导网页授权拿 openid，再唤起支付。
    async payWeixinJsapi() {
      let order;
      try {
        order = await this.createOrderFor("JSAPI", "WeChatPay");
      } catch (e) {
        if (e && e.message === "WECHAT_AUTH_REQUIRED") {
          await this.redirectToWechatAuth(); // 跳走授权，回来后自动续付
          throw new Error("__CANCELLED__");
        }
        throw e;
      }
      await this.invokeWeixinPay(JSON.parse(order.link));
      this.polling = true;
      await this.confirmByPolling(order.outTradeNo);
    },

    // APP（Tauri 壳）：调原生唤起；桌面壳 stub 失败 → 回退扫码。
    async payWeixinApp() {
      let order;
      try {
        order = await this.createOrderFor("APP", "WeChatPay");
      } catch (e) {
        console.warn("APP 下单失败，回退扫码：", e && e.message);
        await this.payNativeQr("WeChatPay");
        return;
      }
      const ok = await this.invokeAppPay(JSON.parse(order.link));
      if (!ok) {
        await this.payNativeQr("WeChatPay");
        return;
      }
      this.polling = true;
      await this.confirmByPolling(order.outTradeNo);
    },

    // 微信 JSSDK 唤起支付。resolve=支付成功，reject=取消/失败。
    invokeWeixinPay(cfg) {
      return new Promise((resolve, reject) => {
        const run = () => {
          // eslint-disable-next-line no-undef
          WeixinJSBridge.invoke(
            "getBrandWCPayRequest",
            {
              appId: cfg.appId,
              timeStamp: cfg.timeStamp,
              nonceStr: cfg.nonceStr,
              package: cfg.package,
              signType: cfg.signType || "RSA",
              paySign: cfg.paySign,
            },
            (res) => {
              if (res.err_msg === "get_brand_wcpay_request:ok") resolve(true);
              else if (res.err_msg === "get_brand_wcpay_request:cancel")
                reject(new Error("__CANCELLED__"));
              else reject(new Error(res.err_msg || "支付未完成"));
            }
          );
        };
        if (typeof WeixinJSBridge === "undefined") {
          document.addEventListener("WeixinJSBridgeReady", run, false);
        } else {
          run();
        }
      });
    },

    // 原生微信 APP 支付（仅 Tauri 移动壳真正可用）。返回是否成功唤起并支付。
    async invokeAppPay(cfg) {
      if (!isTauri()) return false;
      try {
        const r = await nativeWechatPay(cfg);
        return !!(r && r.ok);
      } catch (e) {
        console.warn("原生唤起微信支付失败（桌面壳暂不支持，回退扫码）：", e);
        return false;
      }
    },

    // 微信授权跳转（snsapi_base 静默，仅取 openid）。记录待支付意图供回来续付。
    async redirectToWechatAuth() {
      let appId = "";
      try {
        const cfg = await wechatPayAppConfig();
        appId = cfg.appId;
      } catch (e) {
        /* ignore */
      }
      if (!appId) {
        this.flashToast(this.t.pay_wechat_auth_fail);
        throw new Error("__CANCELLED__");
      }
      this.savePending({ goodsId: this.selectedId, auth: true });
      const redirect = encodeURIComponent(window.location.href.split("#")[0]);
      window.location.href =
        `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}` +
        `&redirect_uri=${redirect}&response_type=code&scope=snsapi_base&state=zenopay#wechat_redirect`;
    },

    // 关闭扫码弹窗，停止轮询。
    cancelQr() {
      this.showQrModal = false;
      this.polling = false;
      this.loading = false;
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    },

    // 支付成功收口。销毁后不再 emit。
    onPaid() {
      this.polling = false;
      this.showQrModal = false;
      this.loading = false;
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
      this.clearPending();
      this.flashToast(this.t.pay_success_toast);
      if (!this._destroyed) this.$emit("success");
    },

    flashToast(msg) {
      this.toast = msg;
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        if (!this._destroyed) this.toast = "";
      }, 2200);
    },

    // 无 UI 的静默轮询（JSAPI/APP 唤起后向后端确认结果）。
    async confirmByPolling(outTradeNo) {
      for (let i = 0; i < 12 && this.polling && !this._destroyed; i++) {
        try {
          const s = await orderState(outTradeNo);
          if (s && s.trade_state === "SUCCESS") {
            this.onPaid();
            return;
          }
        } catch (e) {
          /* 网络抖动忽略，继续轮询 */
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      this.loading = false;
    },

    // 扫码场景轮询：弹窗关闭、停止标记或组件销毁即结束。
    async pollOrder(outTradeNo) {
      const maxRetries = 30;
      for (let count = 0; count < maxRetries; count++) {
        if (!this.showQrModal || !this.polling || this._destroyed) return;
        try {
          const s = await orderState(outTradeNo);
          if (s && s.trade_state === "SUCCESS") {
            this.onPaid();
            return;
          }
        } catch (e) {
          console.error("Poll order status failed", e);
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (this.showQrModal && !this._destroyed) {
        this.showQrModal = false;
        this.flashToast(this.t.pay_unpaid);
      }
      this.polling = false;
      this.loading = false;
    },
  },
  beforeDestroy() {
    // 清理所有定时器/轮询，销毁后不再触发状态更新或 $emit。
    this._destroyed = true;
    this.polling = false;
    clearTimeout(this._pollTimer);
    clearTimeout(this._toastTimer);
    this._pollTimer = null;
    this._toastTimer = null;
  },
};
</script>
